import {
    MoneyMapAccountStatus,
    MoneyMapAccountType,
    type MoneyMapRuleWithAccount,
} from '@fruitful/types';

import {
    type BankAccountDisplayDetail,
    type DepositAccount,
    type IncomeSplitConfigLegacy,
    type IncomeSplitRuleLegacy,
    IncomeSplitRuleType,
    type PaycheckRoutingRuleAccountMetadata,
    type ProposedAccount,
} from '../types/IncomeSplit';
import {
    hasCashCreateParameters,
    type IncomeSplitRuleAccount,
    isBankAccountDisplayDetail,
} from './incomeSplitAccountUtils';

export const formatIncomeSplitPercentages = (
    incomeSplit: IncomeSplitConfigLegacy,
): IncomeSplitConfigLegacy => {
    return {
        ...incomeSplit,
        rules: incomeSplit.rules.map((rule) => {
            const { percentage, type, amount } = rule;
            return {
                ...rule,
                percentage:
                    type === IncomeSplitRuleType.PERCENTAGE
                        ? amount === 100
                            ? null
                            : amount
                        : percentage != null
                          ? Math.floor(percentage * 100)
                          : null,
            };
        }),
    };
};

export const hasConditions = (rule: IncomeSplitRuleLegacy): boolean => {
    const { type, monthlyContributionMaxCents, yearlyContributionMaxCents } =
        rule;

    return (
        type === IncomeSplitRuleType.MINIMUM_BALANCE ||
        (monthlyContributionMaxCents ?? 0) > 0 ||
        (yearlyContributionMaxCents ?? 0) > 0
    );
};

export const calculateFinalizedAmount = (
    rule: IncomeSplitRuleLegacy,
): string => {
    const { percentage, type, amount } = rule;

    const finalizedAmount =
        percentage != null
            ? `${percentage}`
            : type === IncomeSplitRuleType.PERCENTAGE
              ? // Since we default amount to 0, we need to show 100% if amount isn't provided
                amount === 100 || amount === 0
                  ? '100'
                  : `${amount}`
              : '100';

    return `${finalizedAmount}% of remaining funds`;
};

export const getAccountFromRule = (
    rule: IncomeSplitRuleLegacy,
    bankAccountDetails: IncomeSplitRuleAccount[],
    externalAccounts: DepositAccount[],
    moneyMapRulesWithProposedAccounts?: MoneyMapRuleWithAccount[],
):
    | PaycheckRoutingRuleAccountMetadata
    | BankAccountDisplayDetail
    | DepositAccount
    | ProposedAccount
    | undefined => {
    // the account might be proposed, so we'll need to fetch the account details from the money map rule it belongs to
    const moneyMapRuleWithProposedAccount =
        moneyMapRulesWithProposedAccounts?.find((moneyMapRule) => {
            const underlyingAccount =
                moneyMapRule.moneyMapAccount.moneyMapAccountForTransfer ??
                moneyMapRule.moneyMapAccount;
            return (
                underlyingAccount.type ===
                    MoneyMapAccountType.INTERNAL_BANKING &&
                underlyingAccount.status === MoneyMapAccountStatus.PROPOSED &&
                moneyMapRule.id === rule.moneyMapRuleId
            );
        });
    if (moneyMapRuleWithProposedAccount) {
        const underlyingAccount =
            moneyMapRuleWithProposedAccount.moneyMapAccount
                .moneyMapAccountForTransfer ??
            moneyMapRuleWithProposedAccount.moneyMapAccount;

        const { createParameters } = underlyingAccount;
        if (!hasCashCreateParameters(createParameters)) {
            return undefined;
        }

        const { name, icon, color } = createParameters;
        return {
            nickname: name,
            icon,
            color,
        } satisfies ProposedAccount;
    }

    return (
        rule.accountMetadata ??
        bankAccountDetails.find(
            (account) =>
                isBankAccountDisplayDetail(account) &&
                account.bankAccountId === rule.accountId,
        ) ??
        externalAccounts.find((account) => account.id === rule.accountId)
    );
};
